import { useEffect, useState } from 'react';
import axios from 'axios';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, Save, Plus, Trash2, Star, X, ArrowUp, ArrowDown, MessageSquare, HelpCircle,
  Phone, Image as ImageIcon, Layout, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/components/ProtectedRoute';
import { PageMeta } from '@/components/PageMeta';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { API } from '@/lib/api';


const HERO_PAGES = [
  { key: '/', label: "Accueil — Hero avec portrait" },
  { key: '/about', label: "À propos — Portrait Nicolas" },
  { key: '/programmes', label: "Programmes — Image Hero" },
  { key: '/tarifs', label: "Tarifs — Image Hero" },
  { key: '/planning', label: "Planning — Image Hero" },
  { key: '/contact', label: "Contact — Image Hero" },
  { key: '/blog', label: "Blog — Image Hero" },
];

const GALLERY_SIZES = [
  { value: 'square', label: 'Carré' },
  { value: 'tall', label: 'Vertical (hauteur double)' },
  { value: 'wide', label: 'Large (2 colonnes)' },
];

function SectionHeader({ title, highlight }) {
  return (
    <div className="mb-6">
      <h2 className="font-heading text-2xl md:text-3xl text-dark tracking-wide">
        {title}{' '}
        <span className="text-gradient-primary italic font-display inline-block pr-2">{highlight}</span>
      </h2>
    </div>
  );
}

// ---------- Testimonials Tab ----------
function TestimonialsTab() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/admin/testimonials`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = (idx, patch) => {
    const n = [...items];
    n[idx] = { ...n[idx], ...patch };
    setItems(n);
  };
  const move = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const n = [...items];
    [n[idx], n[ni]] = [n[ni], n[idx]];
    setItems(n);
  };
  const add = () => setItems([...items, {
    id: crypto.randomUUID(), name: 'Nouveau membre', role: '', text: '', rating: 5, order: items.length + 1, active: true,
  }]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = items.map((t, i) => ({ ...t, order: i + 1 }));
      await axios.put(`${API}/content/admin/testimonials`, payload);
      toast.success('Témoignages enregistrés');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!items) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <>
      <SectionHeader title="GÉRER LES" highlight="TÉMOIGNAGES" />
      <div className="flex justify-between mb-4">
        <p className="text-sm text-muted-foreground">{items.length} témoignage{items.length > 1 ? 's' : ''}</p>
        <Button onClick={add} variant="outline" size="sm" className="rounded-xl" data-testid="add-testimonial">
          <Plus className="h-4 w-4 mr-1.5" /> Ajouter
        </Button>
      </div>
      <div className="space-y-3 mb-6">
        {items.map((t, idx) => (
          <Card key={t.id} className="p-5 rounded-2xl" data-testid={`testimonial-row-${idx}`}>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Nom</Label>
                <Input value={t.name} onChange={(e) => update(idx, { name: e.target.value })} className="h-10 rounded-xl" />
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Rôle / Ancienneté</Label>
                <Input value={t.role} onChange={(e) => update(idx, { role: e.target.value })} className="h-10 rounded-xl" placeholder="Membre depuis 1 an" />
              </div>
            </div>
            <div className="mb-3">
              <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Témoignage</Label>
              <Textarea value={t.text} onChange={(e) => update(idx, { text: e.target.value })} rows={3} className="rounded-xl" />
            </div>
            <div className="flex flex-wrap items-center gap-4 pt-3 border-t">
              <div className="flex items-center gap-1">
                <Label className="text-xs mr-2">Note</Label>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => update(idx, { rating: n })} className="p-1">
                    <Star className={`h-4 w-4 ${n <= t.rating ? 'text-primary fill-primary' : 'text-muted-foreground'}`} />
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={t.active} onCheckedChange={(v) => update(idx, { active: v })} />
                <Label className="text-xs">Actif</Label>
              </div>
              <div className="ml-auto flex gap-1">
                <Button type="button" variant="outline" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => move(idx, 1)} disabled={idx === items.length - 1}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => remove(idx)} className="text-destructive hover:bg-destructive/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="btn-shine w-full bg-primary hover:bg-primary-light rounded-xl py-6" data-testid="save-testimonials">
        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER LES TÉMOIGNAGES</>}
      </Button>
    </>
  );
}

// ---------- FAQs Tab ----------
function FAQsTab() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/admin/faqs`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = (idx, patch) => {
    const n = [...items]; n[idx] = { ...n[idx], ...patch }; setItems(n);
  };
  const move = (idx, dir) => {
    const ni = idx + dir; if (ni < 0 || ni >= items.length) return;
    const n = [...items]; [n[idx], n[ni]] = [n[ni], n[idx]]; setItems(n);
  };
  const add = () => setItems([...items, {
    id: crypto.randomUUID(), question: 'Nouvelle question ?', answer: '', order: items.length + 1, active: true,
  }]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const save = async () => {
    if (saving) return; setSaving(true);
    try {
      const payload = items.map((t, i) => ({ ...t, order: i + 1 }));
      await axios.put(`${API}/content/admin/faqs`, payload);
      toast.success('FAQ enregistrée');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!items) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <>
      <SectionHeader title="GÉRER LA" highlight="FAQ" />
      <div className="flex justify-between mb-4">
        <p className="text-sm text-muted-foreground">{items.length} question{items.length > 1 ? 's' : ''}</p>
        <Button onClick={add} variant="outline" size="sm" className="rounded-xl" data-testid="add-faq">
          <Plus className="h-4 w-4 mr-1.5" /> Ajouter
        </Button>
      </div>
      <div className="space-y-3 mb-6">
        {items.map((f, idx) => (
          <Card key={f.id} className="p-5 rounded-2xl" data-testid={`faq-row-${idx}`}>
            <div className="mb-3">
              <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Question</Label>
              <Input value={f.question} onChange={(e) => update(idx, { question: e.target.value })} className="h-10 rounded-xl" />
            </div>
            <div className="mb-3">
              <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Réponse</Label>
              <Textarea value={f.answer} onChange={(e) => update(idx, { answer: e.target.value })} rows={3} className="rounded-xl" />
            </div>
            <div className="flex items-center gap-4 pt-3 border-t">
              <div className="flex items-center gap-2">
                <Switch checked={f.active} onCheckedChange={(v) => update(idx, { active: v })} />
                <Label className="text-xs">Visible</Label>
              </div>
              <div className="ml-auto flex gap-1">
                <Button type="button" variant="outline" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => move(idx, 1)} disabled={idx === items.length - 1}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => remove(idx)} className="text-destructive hover:bg-destructive/10">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="btn-shine w-full bg-primary hover:bg-primary-light rounded-xl py-6" data-testid="save-faqs">
        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER LA FAQ</>}
      </Button>
    </>
  );
}

// ---------- Contact Info Tab ----------
function ContactInfoTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/contact-info`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = (key) => (e) => setData({ ...data, [key]: e.target.value });

  const save = async () => {
    if (saving) return; setSaving(true);
    try {
      await axios.put(`${API}/content/admin/contact-info`, data);
      toast.success('Informations enregistrées');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <>
      <SectionHeader title="INFORMATIONS DE" highlight="CONTACT" />
      <Card className="p-6 rounded-2xl space-y-4 mb-6">
        <div>
          <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Adresse complète</Label>
          <Input value={data.address} onChange={update('address')} className="h-11 rounded-xl" />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Email de contact</Label>
            <Input value={data.email} onChange={update('email')} className="h-11 rounded-xl" />
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Téléphone (optionnel)</Label>
            <Input value={data.phone} onChange={update('phone')} className="h-11 rounded-xl" placeholder="+33 ..." />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Instagram (URL)</Label>
            <Input value={data.instagram} onChange={update('instagram')} className="h-11 rounded-xl" />
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Facebook (URL)</Label>
            <Input value={data.facebook} onChange={update('facebook')} className="h-11 rounded-xl" placeholder="https://www.facebook.com/..." />
          </div>
        </div>
        <div>
          <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">URL Google Business Profile (booste le SEO local)</Label>
          <Input value={data.google_business_url} onChange={update('google_business_url')} className="h-11 rounded-xl" placeholder="https://maps.app.goo.gl/..." />
          <p className="text-[11px] text-muted-foreground mt-1.5">Récupère le lien depuis ta fiche Google Business (bouton Partager). Utilisé pour générer un bouton "Laisser un avis" qui booste ton SEO local.</p>
        </div>
        <div>
          <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Requête pour la carte Google Maps embed</Label>
          <Input value={data.maps_embed_query} onChange={update('maps_embed_query')} className="h-11 rounded-xl" />
        </div>
      </Card>
      <Button onClick={save} disabled={saving} className="btn-shine w-full bg-primary hover:bg-primary-light rounded-xl py-6" data-testid="save-contact-info">
        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER LES INFOS DE CONTACT</>}
      </Button>
    </>
  );
}

// ---------- Hero Images Tab ----------
function HeroImagesTab() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/hero-images`).then((r) => setData(r.data || {})).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = (key, patch) => {
    setData({ ...data, [key]: { ...(data[key] || {}), ...patch } });
  };

  const save = async () => {
    if (saving) return; setSaving(true);
    try {
      await axios.put(`${API}/content/admin/hero-images`, data);
      toast.success('Images hero enregistrées');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <>
      <SectionHeader title="IMAGES" highlight="HERO" />
      <p className="text-sm text-muted-foreground mb-6">
        Remplace la photo principale de chaque page. Laisse vide pour utiliser l'image par défaut.
      </p>
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        {HERO_PAGES.map((page) => {
          const current = data[page.key] || { image_url: '', alt: '' };
          return (
            <Card key={page.key} className="p-5 rounded-2xl" data-testid={`hero-${page.key.replace('/', 'root')}`}>
              <h3 className="font-heading text-sm tracking-wide mb-3 text-dark">{page.label}</h3>
              <ImageUploader
                value={current.image_url}
                onChange={(url) => update(page.key, { image_url: url })}
                aspect="aspect-[16/10]"
                testId={`hero-image-${page.key}`}
              />
              <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mt-4 mb-1 block">Texte alternatif (SEO)</Label>
              <Input
                value={current.alt || ''}
                onChange={(e) => update(page.key, { alt: e.target.value })}
                className="h-10 rounded-xl"
                placeholder="Ex: Entraînement CrossFit à Saint-Chamond"
              />
            </Card>
          );
        })}
      </div>
      <Button onClick={save} disabled={saving} className="btn-shine w-full bg-primary hover:bg-primary-light rounded-xl py-6" data-testid="save-hero-images">
        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER LES IMAGES HERO</>}
      </Button>
    </>
  );
}

// ---------- Gallery Tab ----------
function GalleryTab() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/admin/gallery`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const update = (idx, patch) => {
    const n = [...items]; n[idx] = { ...n[idx], ...patch }; setItems(n);
  };
  const move = (idx, dir) => {
    const ni = idx + dir; if (ni < 0 || ni >= items.length) return;
    const n = [...items]; [n[idx], n[ni]] = [n[ni], n[idx]]; setItems(n);
  };
  const add = () => setItems([...items, {
    id: crypto.randomUUID(), image_url: '', alt: '', label: 'NOUVEAU', size: 'square', order: items.length + 1, active: true,
  }]);
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const save = async () => {
    if (saving) return; setSaving(true);
    try {
      const invalid = items.find((i) => !i.image_url);
      if (invalid) {
        toast.error('Chaque image de galerie doit avoir une URL. Complète ou supprime les entrées vides.');
        setSaving(false);
        return;
      }
      const payload = items.map((t, i) => ({ ...t, order: i + 1 }));
      await axios.put(`${API}/content/admin/gallery`, payload);
      toast.success('Galerie enregistrée');
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!items) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <>
      <SectionHeader title="GALERIE DE" highlight="L'ACCUEIL" />
      <p className="text-sm text-muted-foreground mb-4">
        Photos affichées dans la section galerie de la page d'accueil. Laisse vide pour utiliser la galerie par défaut.
      </p>
      <div className="flex justify-between mb-4">
        <p className="text-sm text-muted-foreground">{items.length} photo{items.length > 1 ? 's' : ''}</p>
        <Button onClick={add} variant="outline" size="sm" className="rounded-xl" data-testid="add-gallery">
          <Plus className="h-4 w-4 mr-1.5" /> Ajouter
        </Button>
      </div>
      <div className="space-y-4 mb-6">
        {items.map((g, idx) => (
          <Card key={g.id} className="p-5 rounded-2xl" data-testid={`gallery-row-${idx}`}>
            <div className="grid md:grid-cols-[240px_1fr] gap-5">
              <ImageUploader
                value={g.image_url}
                onChange={(url) => update(idx, { image_url: url })}
                aspect="aspect-square"
                testId={`gallery-image-${idx}`}
              />
              <div className="space-y-3">
                <div>
                  <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Label (titre sur l'image)</Label>
                  <Input value={g.label} onChange={(e) => update(idx, { label: e.target.value })} className="h-10 rounded-xl" placeholder="FORCE" />
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Texte alternatif (SEO)</Label>
                  <Input value={g.alt} onChange={(e) => update(idx, { alt: e.target.value })} className="h-10 rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Format</Label>
                    <Select value={g.size} onValueChange={(v) => update(idx, { size: v })}>
                      <SelectTrigger className="h-10 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {GALLERY_SIZES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end gap-2 pb-1">
                    <div className="flex items-center gap-2">
                      <Switch checked={g.active} onCheckedChange={(v) => update(idx, { active: v })} />
                      <Label className="text-xs">Visible</Label>
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 justify-end pt-2 border-t">
                  <Button type="button" variant="outline" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => move(idx, 1)} disabled={idx === items.length - 1}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => remove(idx)} className="text-destructive hover:bg-destructive/10">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="btn-shine w-full bg-primary hover:bg-primary-light rounded-xl py-6" data-testid="save-gallery">
        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER LA GALERIE</>}
      </Button>
    </>
  );
}

// ---------- Reservation Config Tab ----------
function ReservationConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content/reservation-config`).then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/admin/reservation-config`, cfg, { withCredentials: true });
      toast.success('Configuration de réservation enregistrée');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <>
      <SectionHeader title="Réservation" highlight="en ligne" />

      <Card className="p-5 mb-4 bg-accent/30 border-primary/20">
        <div className="flex items-start gap-3">
          <CalendarCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
          <div className="text-sm text-foreground/80 leading-relaxed">
            <p className="font-semibold text-dark mb-1">Deciplus = votre seule source de vérité</p>
            <p className="mb-2">
              Vos horaires, cours et places se gèrent <strong>uniquement dans votre back-office Deciplus</strong>.
              Le site (pages <em>Planning</em> et <em>Réservation</em>) affiche votre planning en direct : toute
              modification faite dans Deciplus apparaît automatiquement sur le site, sans rien toucher ici.
            </p>
            <p className="font-semibold text-dark mb-1">Où trouver mes liens Deciplus ?</p>
            <p>
              Connectez-vous à <em>Deciplus Manager → Espaces Membres</em>, choisissez la vue souhaitée
              (calendrier / planning), puis cliquez sur <strong>Exporter / Copier le lien</strong> de l'iframe.
              Collez ce lien dans les champs ci-dessous.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
          <div>
            <Label className="text-dark font-semibold">Réservation en ligne active</Label>
            <p className="text-xs text-muted-foreground mt-1">Si désactivé, la page affiche un écran "Bientôt disponible" avec CTA contact.</p>
          </div>
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="toggle-reservation-enabled" />
        </div>

        <div>
          <Label htmlFor="embed_url" className="text-dark font-semibold">URL du widget de réservation</Label>
          <Input
            id="embed_url"
            type="url"
            placeholder="https://ta-salle.deciplus.pro/reservation"
            value={cfg.embed_url || ''}
            onChange={(e) => set({ embed_url: e.target.value })}
            className="mt-1.5"
            data-testid="input-embed-url"
          />
          <p className="text-xs text-muted-foreground mt-1.5">L'URL sera chargée dans un iframe sur la page /reservation.</p>
        </div>

        <div>
          <Label htmlFor="planning_url" className="text-dark font-semibold">URL du planning Deciplus (page /planning)</Label>
          <Input
            id="planning_url"
            type="url"
            placeholder="https://member-app.deciplus.pro/cf-saintchamond/tunnel?..."
            value={cfg.planning_url || ''}
            onChange={(e) => set({ planning_url: e.target.value })}
            className="mt-1.5"
            data-testid="input-planning-url"
          />
          <p className="text-xs text-muted-foreground mt-1.5">
            Vue calendrier Deciplus (ex. <code className="bg-muted px-1 rounded">calendarByZone</code>), à exporter depuis <em>Deciplus Manager → Espaces Membres</em>. Si vide, la page /planning utilise l'URL de réservation ci-dessus. <strong>C'est Deciplus qui reste la seule source de vérité</strong> : ton client met à jour ses horaires directement dans Deciplus.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="provider_name" className="text-dark font-semibold">Nom du prestataire</Label>
            <Input
              id="provider_name"
              value={cfg.provider_name || ''}
              onChange={(e) => set({ provider_name: e.target.value })}
              className="mt-1.5"
              data-testid="input-provider-name"
            />
          </div>
          <div>
            <Label htmlFor="height_px" className="text-dark font-semibold">Hauteur iframe (px)</Label>
            <Input
              id="height_px"
              type="number"
              min={400}
              max={2000}
              value={cfg.height_px || 900}
              onChange={(e) => set({ height_px: parseInt(e.target.value, 10) || 900 })}
              className="mt-1.5"
              data-testid="input-height"
            />
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider font-semibold">Écran de secours (si réservation désactivée)</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="fallback_cta_label" className="text-sm">Libellé du bouton</Label>
              <Input
                id="fallback_cta_label"
                value={cfg.fallback_cta_label || ''}
                onChange={(e) => set({ fallback_cta_label: e.target.value })}
                className="mt-1.5"
                data-testid="input-fallback-cta"
              />
            </div>
            <div>
              <Label htmlFor="fallback_message" className="text-sm">Message affiché</Label>
              <Textarea
                id="fallback_message"
                rows={3}
                value={cfg.fallback_message || ''}
                onChange={(e) => set({ fallback_message: e.target.value })}
                className="mt-1.5"
                data-testid="input-fallback-message"
              />
            </div>
          </div>
        </div>
      </Card>

      <Button onClick={save} disabled={saving} className="btn-shine w-full bg-primary hover:bg-primary-light rounded-xl py-6 mt-4" data-testid="save-reservation-config">
        {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enregistrement...</> : <><Save className="h-4 w-4 mr-2" /> ENREGISTRER LA CONFIGURATION</>}
      </Button>
    </>
  );
}

// ---------- Main Page ----------
export default function AdminContentPage() {
  return (
    <AdminLayout>
      <PageMeta title="Contenu admin" description="Gestion du contenu du site" />
      <div className="mb-6">
        <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ Contenu</span>
        <h1 className="font-heading text-4xl md:text-5xl text-dark mt-2">
          GÉRER LE <span className="text-gradient-primary italic font-display inline-block pr-2">CONTENU</span>
        </h1>
        <p className="text-muted-foreground text-sm mt-2">
          Modifie les témoignages, la FAQ, les images et les informations de contact en un clic.
        </p>
      </div>

      <Tabs defaultValue="testimonials" className="w-full">
        <TabsList className="w-full h-auto p-1 bg-muted rounded-xl grid grid-cols-2 md:grid-cols-6 gap-1 mb-6">
          <TabsTrigger value="testimonials" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg py-2.5" data-testid="tab-testimonials">
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" /> Témoignages
          </TabsTrigger>
          <TabsTrigger value="faqs" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg py-2.5" data-testid="tab-faqs">
            <HelpCircle className="h-3.5 w-3.5 mr-1.5" /> FAQ
          </TabsTrigger>
          <TabsTrigger value="contact" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg py-2.5" data-testid="tab-contact">
            <Phone className="h-3.5 w-3.5 mr-1.5" /> Contact
          </TabsTrigger>
          <TabsTrigger value="hero" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg py-2.5" data-testid="tab-hero">
            <Layout className="h-3.5 w-3.5 mr-1.5" /> Hero
          </TabsTrigger>
          <TabsTrigger value="gallery" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg py-2.5" data-testid="tab-gallery">
            <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Galerie
          </TabsTrigger>
          <TabsTrigger value="reservation" className="data-[state=active]:bg-primary data-[state=active]:text-white rounded-lg py-2.5" data-testid="tab-reservation">
            <CalendarCheck className="h-3.5 w-3.5 mr-1.5" /> Réservation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="testimonials"><TestimonialsTab /></TabsContent>
        <TabsContent value="faqs"><FAQsTab /></TabsContent>
        <TabsContent value="contact"><ContactInfoTab /></TabsContent>
        <TabsContent value="hero"><HeroImagesTab /></TabsContent>
        <TabsContent value="gallery"><GalleryTab /></TabsContent>
        <TabsContent value="reservation"><ReservationConfigTab /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
